import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import PencilIcon from '@/assets/icons/pencil-edit-stabilo.svg';
import { ExternalLinkIcon } from '@/components/icons/external-link';
import { type HiringGoal } from '@/components/pages/edit-targets/types';
import { Button } from '@/components/ui/button';
import { Typography } from '@/components/ui/typography';
import { useEditTargetsContext } from '@/contexts/edit-targets-context';
import { formatShortDate } from '@/utils/date-util';

interface DetailItemProps {
  label: string;
  value: React.ReactNode;
}

function DetailItem({ label, value }: DetailItemProps) {
  return (
    <div className="flex flex-col gap-1">
      <Typography variant="caption" className="!text-fx-neutral-400">
        {label}
      </Typography>
      <Typography variant="small2">{value}</Typography>
    </div>
  );
}

interface HiringGoalDetailsProps {
  hiringGoal: HiringGoal;
}

export function HiringGoalDetails({ hiringGoal }: HiringGoalDetailsProps) {
  const intl = useIntl();
  const { hiringGoalUrl } = useEditTargetsContext();
  const {
    openingName,
    startDate,
    goal,
    is_current: isCurrent,
    endDate,
  } = hiringGoal;

  return (
    <>
      <div className="flex min-w-0 flex-col items-start gap-2 self-stretch">
        <Typography variant="body2">{openingName}</Typography>
        {!isCurrent && (
          <Typography variant="small1" className="!text-fx-neutral-600">
            <FormattedMessage
              defaultMessage="Previous <b>Hiring Goal</b> has ended. Please add a new Hiring goal to enable targets"
              description="Note shown when a past hiring goal is selected and a new goal is required for targets"
            />
          </Typography>
        )}
      </div>
      <div className="flex min-w-0 flex-col items-start gap-4 self-stretch">
        {!isCurrent && (
          <Typography variant="caption">
            <FormattedMessage
              defaultMessage="Prev. hiring goal"
              description="Caption on goal picker when showing a historical period"
            />
          </Typography>
        )}
        <div className="flex w-full min-w-0 flex-wrap items-center gap-x-6 gap-y-2">
          <DetailItem
            label={intl.formatMessage({
              defaultMessage: 'Hiring Target',
              description:
                'Numeric goal for how many hires this period should achieve',
            })}
            value={intl.formatMessage(
              {
                defaultMessage: '{goal} hires',
                description:
                  'Displays the integer target with the word “hires” after it',
              },
              { goal },
            )}
          />
          <DetailItem
            label={intl.formatMessage({
              defaultMessage: 'Start Date',
              description: 'When the selected hiring goal period begins',
            })}
            value={formatShortDate(startDate)}
          />

          <DetailItem
            label={intl.formatMessage({
              defaultMessage: 'End Date',
              description: 'When the selected hiring goal period ends',
            })}
            value={
              endDate ? (
                formatShortDate(endDate)
              ) : hiringGoalUrl ? (
                <Typography variant="body2" asChild>
                  <Link
                    to={hiringGoalUrl}
                    target="_blank"
                    className="text-fx-primary inline-flex items-center gap-1 text-[length:inherit] underline"
                  >
                    <FormattedMessage
                      defaultMessage="+ Add"
                      description="Link to add an end date to the hiring goal"
                    />
                    <ExternalLinkIcon className="size-3 shrink-0 text-current" />
                  </Link>
                </Typography>
              ) : null
            }
          />

          {isCurrent && hiringGoalUrl && (
            <Button
              variant="secondary"
              asChild
              className="min-h-9 ml-auto h-auto min-w-0 max-w-full shrink-0 basis-auto whitespace-normal py-2.5"
            >
              <Link
                to={hiringGoalUrl ?? ''}
                target="_blank"
                className="!inline-flex min-w-0 max-w-full items-start gap-2"
              >
                <img src={PencilIcon} alt="" className="mt-0.5 shrink-0" />
                <span className="min-w-0 max-w-full break-words text-left leading-snug">
                  <FormattedMessage
                    defaultMessage="Edit hiring goal"
                    description="Link text to open hiring goal settings in another app"
                  />
                </span>
              </Link>
            </Button>
          )}
        </div>
      </div>
    </>
  );
}
